import { useState } from 'react'
import { Link } from '@tanstack/react-router'
import { ArrowLeft, ArrowRight, ExternalLink, Plus } from 'lucide-react'
import { toast } from 'sonner'
import { Button, Chip, Select } from '@/components/ui'
import { odooUrl } from '@/lib/utils/odoo-url'
import { useHasAnyPermission } from '@/shell/module-system/permissions'
import {
  useFreightPipelineDetail,
  useFreightShipmentDetail,
  useQuotePipeline,
  useTenderPipeline,
  useTransitionPipeline,
} from '../api/queries'
import type { FreightQuoteOption, PipelineStage, QuoteRequestBody } from '../api/types'
import { STAGE_LABELS, STAGE_VARIANT, manualTransitionTargetsFrom } from '../utils/pipeline-stage-meta'
import { CarrierComparisonTable } from './CarrierComparisonTable'
import { DocumentViewer } from './DocumentViewer'
import { QuoteRequestForm } from './QuoteRequestForm'
import { StageHistoryTimeline } from './StageHistoryTimeline'
import { TenderConfirmModal } from './TenderConfirmModal'

export interface LogisticsPipelineWorkViewProps {
  pipelineId: string
}

/**
 * Logistics overhaul Phase 2b — quote/tender work view for one
 * freight_pipeline row. Quote request + carrier comparison + tender are
 * all live here now; Phase 2a shipped the read/navigate-only shell first.
 *
 * The last submitted quote request (origin/destination/commodities/pickup
 * date) is kept in local state so the tender call carries the same shipment
 * data the quote was priced against — the backend doesn't persist the
 * request itself, only the returned `quote_options`.
 */
export function LogisticsPipelineWorkView({ pipelineId }: LogisticsPipelineWorkViewProps) {
  const detail = useFreightPipelineDetail(pipelineId)
  const data = detail.data
  const shipment = useFreightShipmentDetail(data?.freight_shipment_id ?? null)
  const transition = useTransitionPipeline()
  const quote = useQuotePipeline()
  const tender = useTenderPipeline()
  const canTender = useHasAnyPermission(['admin', 'operations_logistics'])

  const [quoteFormOpen, setQuoteFormOpen] = useState(false)
  const [lastQuoteRequest, setLastQuoteRequest] = useState<QuoteRequestBody | null>(null)
  const [tenderTarget, setTenderTarget] = useState<FreightQuoteOption | null>(null)

  if (detail.isLoading || !data) {
    return (
      <div data-density="comfortable" className="flex h-full flex-col">
        <WorkViewHeader />
        <div className="min-h-0 flex-1 overflow-auto p-8 text-tertiary">
          {detail.isLoading ? 'Loading…' : 'No data'}
        </div>
      </div>
    )
  }

  const manualTargets = manualTransitionTargetsFrom(data.stage)
  const notYetTendered = !data.freight_shipment_id

  function handleQuoteSubmit(body: QuoteRequestBody) {
    setLastQuoteRequest(body)
    quote.mutate(
      { pipelineId, body },
      {
        onSuccess: () => setQuoteFormOpen(false),
        onError: (err) => {
          const msg = err instanceof Error ? err.message : 'quote request failed'
          toast.error(msg, { duration: 6000 })
        },
      },
    )
  }

  function handleTenderConfirm() {
    if (!tenderTarget) return
    tender.mutate(
      {
        pipelineId,
        body: {
          provider: tenderTarget.provider,
          rate_id: tenderTarget.rate_id,
          quote_id: tenderTarget.quote_id,
          carrier_name: tenderTarget.carrier_name,
          carrier_scac: tenderTarget.carrier_scac,
          pickup_date: lastQuoteRequest?.pickup_date,
          origin: lastQuoteRequest?.origin,
          destination: lastQuoteRequest?.destination,
          commodities: lastQuoteRequest?.commodities,
        },
      },
      {
        onSuccess: () => {
          toast.success(`Tendered with ${tenderTarget.carrier_name ?? tenderTarget.provider}`)
          setTenderTarget(null)
        },
        onError: (err) => {
          const msg = err instanceof Error ? err.message : 'tender failed'
          toast.error(msg, { duration: 8000 })
          setTenderTarget(null)
        },
      },
    )
  }

  return (
    <div data-density="comfortable" className="flex h-full flex-col bg-canvas">
      <header className="flex items-center gap-3 border-b border-border-subtle bg-surface px-6 py-3">
        <Link to="/logistics">
          <Button size="sm" variant="ghost">
            <ArrowLeft size={14} />
            SPI Freight
          </Button>
        </Link>
        <div className="min-w-0">
          <h1 className="text-role-data-primary truncate">
            <a
              href={odooUrl('purchase.order', data.odoo_po_id)}
              target="_blank"
              rel="noopener noreferrer"
              className="text-accent-text hover:underline"
            >
              {data.odoo_po_name}
            </a>
          </h1>
        </div>
        <div className="ml-2 flex items-center gap-2">
          <Chip size="xs" variant={STAGE_VARIANT[data.stage]} dot>
            {STAGE_LABELS[data.stage]}
          </Chip>
          {data.requested_provider ? (
            <span className="text-xs text-tertiary">{data.requested_provider}</span>
          ) : null}
        </div>
        {notYetTendered ? (
          <div className="ml-auto">
            <Button variant="primary" size="sm" onClick={() => setQuoteFormOpen(true)}>
              <Plus size={14} className="mr-1.5" />
              Get quote
            </Button>
          </div>
        ) : null}
      </header>

      <div className="min-h-0 flex-1 overflow-y-auto px-6 py-4">
        <div className="mx-auto flex max-w-3xl flex-col gap-4">
          <section className="rounded-sm border border-border-subtle bg-surface p-3">
            <h3 className="text-role-section-label m-0 mb-2">Pipeline</h3>
            <dl className="grid grid-cols-2 gap-3 text-sm">
              <div>
                <dt className="text-role-metadata text-tertiary">Odoo PO</dt>
                <dd className="m-0">
                  <a
                    href={odooUrl('purchase.order', data.odoo_po_id)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-accent-text hover:underline"
                  >
                    {data.odoo_po_name}
                    <ExternalLink size={12} />
                  </a>
                </dd>
              </div>
              <div>
                <dt className="text-role-metadata text-tertiary">Requested carrier</dt>
                <dd className="m-0">{data.requested_provider ?? '—'}</dd>
              </div>
              <div>
                <dt className="text-role-metadata text-tertiary">Created by</dt>
                <dd className="m-0">{data.created_by ?? '—'}</dd>
              </div>
              <div>
                <dt className="text-role-metadata text-tertiary">Created</dt>
                <dd className="m-0">{formatDateTime(data.created_at)}</dd>
              </div>
            </dl>
          </section>

          {data.quote_options && data.quote_options.length > 0 ? (
            <section className="rounded-sm border border-border-subtle bg-surface p-3">
              <h3 className="text-role-section-label m-0 mb-2">
                Compared quotes ({data.quote_options.length})
              </h3>
              <CarrierComparisonTable
                options={data.quote_options}
                onTender={setTenderTarget}
                tenderDisabled={!notYetTendered || tender.isPending}
              />
              {!canTender ? (
                <p className="m-0 mt-2 text-xs text-tertiary">
                  Your role can compare quotes but not tender — ask an admin or a Logistics-enabled
                  operator to book.
                </p>
              ) : null}
            </section>
          ) : null}

          {data.freight_shipment_id ? (
            <section className="rounded-sm border border-border-subtle bg-surface p-3">
              <h3 className="text-role-section-label m-0 mb-2">Shipment tracking</h3>
              {shipment.isLoading || !shipment.data ? (
                <p className="m-0 text-sm text-tertiary">Loading tracking…</p>
              ) : (
                <dl className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <dt className="text-role-metadata text-tertiary">Carrier</dt>
                    <dd className="m-0">
                      {shipment.data.carrier_name ?? '—'}
                      {shipment.data.carrier_scac ? ` (${shipment.data.carrier_scac})` : ''}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-role-metadata text-tertiary">Reference</dt>
                    <dd className="m-0">{shipment.data.provider_ref}</dd>
                  </div>
                  <div>
                    <dt className="text-role-metadata text-tertiary">Last location</dt>
                    <dd className="m-0">{shipment.data.last_location ?? '—'}</dd>
                  </div>
                  <div>
                    <dt className="text-role-metadata text-tertiary">Est. delivery</dt>
                    <dd className="m-0">{shipment.data.est_delivery ?? '—'}</dd>
                  </div>
                </dl>
              )}
              <div className="mt-3">
                <DocumentViewer shipmentId={data.freight_shipment_id} />
              </div>
              <Link
                to="/logistics"
                className="mt-2 inline-block text-xs text-info hover:underline"
              >
                View full tracking in SPI Freight →
              </Link>
            </section>
          ) : null}

          {manualTargets.length > 0 ? (
            <section className="rounded-sm border border-border-subtle bg-surface p-3">
              <h3 className="text-role-section-label m-0 mb-2">Move pipeline</h3>
              <PipelineTransitionPicker
                targets={manualTargets}
                isPending={transition.isPending}
                onChoose={(target, note) => {
                  transition.mutate(
                    { pipelineId, targetStage: target, note },
                    {
                      onError: (err) => {
                        const msg = err instanceof Error ? err.message : 'transition failed'
                        toast.error(msg, { duration: 5000 })
                      },
                    },
                  )
                }}
              />
              <p className="m-0 mt-2 text-xs text-tertiary">
                Tendering (booking a rate) isn&apos;t available from this picker — use the
                comparison table above once you have a quote.
              </p>
            </section>
          ) : null}

          <section className="rounded-sm border border-border-subtle bg-surface p-3">
            <h3 className="text-role-section-label m-0 mb-2">History</h3>
            <StageHistoryTimeline entries={data.stage_history} />
          </section>
        </div>
      </div>

      <QuoteRequestForm
        open={quoteFormOpen}
        defaultProvider={data.requested_provider}
        isPending={quote.isPending}
        onClose={() => setQuoteFormOpen(false)}
        onSubmit={handleQuoteSubmit}
      />

      <TenderConfirmModal
        option={tenderTarget}
        odooPoName={data.odoo_po_name}
        isPending={tender.isPending}
        onCancel={() => setTenderTarget(null)}
        onConfirm={handleTenderConfirm}
      />
    </div>
  )
}

function WorkViewHeader() {
  return (
    <header className="flex items-center gap-3 border-b border-border-subtle bg-surface px-6 py-3">
      <Link to="/logistics">
        <Button size="sm" variant="ghost">
          <ArrowLeft size={14} />
          SPI Freight
        </Button>
      </Link>
      <span className="text-role-section-label">Freight pipeline</span>
    </header>
  )
}

function PipelineTransitionPicker({
  targets,
  isPending,
  onChoose,
}: {
  targets: PipelineStage[]
  isPending: boolean
  onChoose: (target: PipelineStage, note?: string) => void
}) {
  const [target, setTarget] = useState<PipelineStage | ''>('')
  const [note, setNote] = useState('')

  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-wrap items-center gap-2">
        <Select
          value={target}
          onChange={(e) => setTarget(e.target.value as PipelineStage | '')}
          aria-label="Target stage"
          className="min-w-[14rem]"
        >
          <option value="">Choose target stage…</option>
          {targets.map((t) => (
            <option key={t} value={t}>
              {STAGE_LABELS[t]}
            </option>
          ))}
        </Select>
        <input
          type="text"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="Optional note (why)"
          className="min-w-[16rem] flex-1 rounded-xs border border-border-default bg-surface px-2 py-1 text-sm text-primary focus:border-border-focus focus:outline-none"
        />
        <Button
          variant="secondary"
          size="sm"
          disabled={!target || isPending}
          onClick={() => {
            if (!target) return
            onChoose(target, note.trim() || undefined)
            setTarget('')
            setNote('')
          }}
        >
          <ArrowRight size={14} className="mr-1.5" />
          Move
        </Button>
      </div>
    </div>
  )
}

function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return '—'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso
  return d.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })
}
